'use client';

import {
  Children,
  cloneElement,
  memo,
  type ReactElement,
  type ElementType,
} from 'react';

import {
  ButtonBase,
  type ButtonBaseSharedProps,
  type SpinnerSizeValue,
} from '../../internal/components/button-base/ButtonBase';
import { useLoadingAnnouncement } from '../../internal/hooks/useLoadingAnnouncement';
import {
  type PolymorphicComponent,
  type PolymorphicComponentProps,
} from '../../types/component';
import { type ResponsiveOption } from '../../types/responsiveOption';
import { resolveResponsiveProps } from '../../utils/resolveResponsiveProps';
import { tv } from '../../utils/tv';
import { Spinner } from '../spinner/Spinner';

type ButtonBaseProps = ButtonBaseSharedProps & {
  /** @default 'filled' */
  variant?: ButtonBaseSharedProps['variant'];
  /**
   * Icon to display at the left of the button (before text).
   */
  leftIcon?: ReactElement;
  /**
   * Icon to display at the right of the button (after text).
   */
  rightIcon?: ReactElement;
  /**
   * Whether the button should take the full width of its container.
   * @default false
   */
  isFullWidth?: boolean;
};

const DEFAULT_ELEMENT_TYPE = 'button';

const buttonClasses = {
  root: 'zds-button-root',
  content: 'zds-button-content',
  leftIcon: 'zds-button-left-icon',
  rightIcon: 'zds-button-right-icon',
  spinner: 'zds-button-spinner',
  label: 'zds-button-label',
} as const;

const buttonStyles = tv({
  slots: {
    root: [buttonClasses.root],
    content: [
      buttonClasses.content,
      'relative',
      'flex',
      'items-center',
      'justify-center',
    ],
    leftIcon: [buttonClasses.leftIcon, 'text-inherit'],
    rightIcon: [buttonClasses.rightIcon, 'text-inherit'],
    spinner: [buttonClasses.spinner, 'absolute', 'inset-0', 'm-auto'],
    label: [buttonClasses.label, 'z--button-font', 'z--button-font-weight'],
  },
  variants: {
    variant: {
      // Base and colour styles are applied by ButtonBase; these entries exist
      // only to enable the ghost-padding compound variants below.
      filled: {},
      outlined: {},
      ghost: {},
    },
    size: {
      sm: {
        root: 'z--button-sm-padding-left z--button-sm-padding-right',
        content: 'z--button-sm-gap z--button-sm-font-size',
        // Icon size is controlled via tokens rather than the Icon component's size prop.
        // In the Figma design, the designer detached the icon component so that icon size
        // can only be adjusted through design tokens — preventing arbitrary overrides.
        // This implementation mirrors that approach: using dedicated tokens also decouples
        // icon sizing from the Icon component's own size scale, which is more flexible.
        leftIcon:
          'z--button-sm-left-icon-margin-left z--button-base-sm-icon-height z--button-base-sm-icon-width',
        rightIcon:
          'z--button-sm-right-icon-margin-right z--button-base-sm-icon-height z--button-base-sm-icon-width',
      },
      md: {
        root: 'z--button-md-padding-left z--button-md-padding-right',
        content: 'z--button-md-gap z--button-md-font-size',
        leftIcon:
          'z--button-md-left-icon-margin-left z--button-base-md-icon-height z--button-base-md-icon-width',
        rightIcon:
          'z--button-md-right-icon-margin-right z--button-base-md-icon-height z--button-base-md-icon-width',
      },
      lg: {
        root: 'z--button-lg-padding-left z--button-lg-padding-right',
        content: 'z--button-lg-gap z--button-lg-font-size',
        leftIcon:
          'z--button-lg-left-icon-margin-left z--button-base-lg-icon-height z--button-base-lg-icon-width',
        rightIcon:
          'z--button-lg-right-icon-margin-right z--button-base-lg-icon-height z--button-base-lg-icon-width',
      },
      xl: {
        root: 'z--button-xl-padding-left z--button-xl-padding-right',
        content: 'z--button-xl-gap z--button-xl-font-size',
        leftIcon:
          'z--button-xl-left-icon-margin-left z--button-base-xl-icon-height z--button-base-xl-icon-width',
        rightIcon:
          'z--button-xl-right-icon-margin-right z--button-base-xl-icon-height z--button-base-xl-icon-width',
      },
    },
    sizeXs: {
      sm: {
        root: 'xs:xs:z--button-sm-padding-left xs:z--button-sm-padding-right',
        content: 'xs:z--button-sm-gap xs:z--button-sm-font-size',
        leftIcon:
          'xs:z--button-sm-left-icon-margin-left xs:z--button-base-sm-icon-height xs:z--button-base-sm-icon-width',
        rightIcon:
          'xs:z--button-sm-right-icon-margin-right xs:z--button-base-sm-icon-height xs:z--button-base-sm-icon-width',
      },
      md: {
        root: 'xs:xs:z--button-md-padding-left xs:z--button-md-padding-right',
        content: 'xs:z--button-md-gap xs:z--button-md-font-size',
        leftIcon:
          'xs:z--button-md-left-icon-margin-left xs:z--button-base-md-icon-height xs:z--button-base-md-icon-width',
        rightIcon:
          'xs:z--button-md-right-icon-margin-right xs:z--button-base-md-icon-height xs:z--button-base-md-icon-width',
      },
      lg: {
        root: 'xs:xs:z--button-lg-padding-left xs:z--button-lg-padding-right',
        content: 'xs:z--button-lg-gap xs:z--button-lg-font-size',
        leftIcon:
          'xs:z--button-lg-left-icon-margin-left xs:z--button-base-lg-icon-height xs:z--button-base-lg-icon-width',
        rightIcon:
          'xs:z--button-lg-right-icon-margin-right xs:z--button-base-lg-icon-height xs:z--button-base-lg-icon-width',
      },
      xl: {
        root: 'xs:xs:z--button-xl-padding-left xs:z--button-xl-padding-right',
        content: 'xs:z--button-xl-gap xs:z--button-xl-font-size',
        leftIcon:
          'xs:z--button-xl-left-icon-margin-left xs:z--button-base-xl-icon-height xs:z--button-base-xl-icon-width',
        rightIcon:
          'xs:z--button-xl-right-icon-margin-right xs:z--button-base-xl-icon-height xs:z--button-base-xl-icon-width',
      },
    },
    sizeSm: {
      sm: {
        root: 'sm:sm:z--button-sm-padding-left sm:z--button-sm-padding-right',
        content: 'sm:z--button-sm-gap sm:z--button-sm-font-size',
        leftIcon:
          'sm:z--button-sm-left-icon-margin-left sm:z--button-base-sm-icon-height sm:z--button-base-sm-icon-width',
        rightIcon:
          'sm:z--button-sm-right-icon-margin-right sm:z--button-base-sm-icon-height sm:z--button-base-sm-icon-width',
      },
      md: {
        root: 'sm:sm:z--button-md-padding-left sm:z--button-md-padding-right',
        content: 'sm:z--button-md-gap sm:z--button-md-font-size',
        leftIcon:
          'sm:z--button-md-left-icon-margin-left sm:z--button-base-md-icon-height sm:z--button-base-md-icon-width',
        rightIcon:
          'sm:z--button-md-right-icon-margin-right sm:z--button-base-md-icon-height sm:z--button-base-md-icon-width',
      },
      lg: {
        root: 'sm:sm:z--button-lg-padding-left sm:z--button-lg-padding-right',
        content: 'sm:z--button-lg-gap sm:z--button-lg-font-size',
        leftIcon:
          'sm:z--button-lg-left-icon-margin-left sm:z--button-base-lg-icon-height sm:z--button-base-lg-icon-width',
        rightIcon:
          'sm:z--button-lg-right-icon-margin-right sm:z--button-base-lg-icon-height sm:z--button-base-lg-icon-width',
      },
      xl: {
        root: 'sm:sm:z--button-xl-padding-left sm:z--button-xl-padding-right',
        content: 'sm:z--button-xl-gap sm:z--button-xl-font-size',
        leftIcon:
          'sm:z--button-xl-left-icon-margin-left sm:z--button-base-xl-icon-height sm:z--button-base-xl-icon-width',
        rightIcon:
          'sm:z--button-xl-right-icon-margin-right sm:z--button-base-xl-icon-height sm:z--button-base-xl-icon-width',
      },
    },
    sizeMd: {
      sm: {
        root: 'md:md:z--button-sm-padding-left md:z--button-sm-padding-right',
        content: 'md:z--button-sm-gap md:z--button-sm-font-size',
        leftIcon:
          'md:z--button-sm-left-icon-margin-left md:z--button-base-sm-icon-height md:z--button-base-sm-icon-width',
        rightIcon:
          'md:z--button-sm-right-icon-margin-right md:z--button-base-sm-icon-height md:z--button-base-sm-icon-width',
      },
      md: {
        root: 'md:md:z--button-md-padding-left md:z--button-md-padding-right',
        content: 'md:z--button-md-gap md:z--button-md-font-size',
        leftIcon:
          'md:z--button-md-left-icon-margin-left md:z--button-base-md-icon-height md:z--button-base-md-icon-width',
        rightIcon:
          'md:z--button-md-right-icon-margin-right md:z--button-base-md-icon-height md:z--button-base-md-icon-width',
      },
      lg: {
        root: 'md:md:z--button-lg-padding-left md:z--button-lg-padding-right',
        content: 'md:z--button-lg-gap md:z--button-lg-font-size',
        leftIcon:
          'md:z--button-lg-left-icon-margin-left md:z--button-base-lg-icon-height md:z--button-base-lg-icon-width',
        rightIcon:
          'md:z--button-lg-right-icon-margin-right md:z--button-base-lg-icon-height md:z--button-base-lg-icon-width',
      },
      xl: {
        root: 'md:md:z--button-xl-padding-left md:z--button-xl-padding-right',
        content: 'md:z--button-xl-gap md:z--button-xl-font-size',
        leftIcon:
          'md:z--button-xl-left-icon-margin-left md:z--button-base-xl-icon-height md:z--button-base-xl-icon-width',
        rightIcon:
          'md:z--button-xl-right-icon-margin-right md:z--button-base-xl-icon-height md:z--button-base-xl-icon-width',
      },
    },
    sizeLg: {
      sm: {
        root: 'lg:lg:z--button-sm-padding-left lg:z--button-sm-padding-right',
        content: 'lg:z--button-sm-gap lg:z--button-sm-font-size',
        leftIcon:
          'lg:z--button-sm-left-icon-margin-left lg:z--button-base-sm-icon-height lg:z--button-base-sm-icon-width',
        rightIcon:
          'lg:z--button-sm-right-icon-margin-right lg:z--button-base-sm-icon-height lg:z--button-base-sm-icon-width',
      },
      md: {
        root: 'lg:lg:z--button-md-padding-left lg:z--button-md-padding-right',
        content: 'lg:z--button-md-gap lg:z--button-md-font-size',
        leftIcon:
          'lg:z--button-md-left-icon-margin-left lg:z--button-base-md-icon-height lg:z--button-base-md-icon-width',
        rightIcon:
          'lg:z--button-md-right-icon-margin-right lg:z--button-base-md-icon-height lg:z--button-base-md-icon-width',
      },
      lg: {
        root: 'lg:lg:z--button-lg-padding-left lg:z--button-lg-padding-right',
        content: 'lg:z--button-lg-gap lg:z--button-lg-font-size',
        leftIcon:
          'lg:z--button-lg-left-icon-margin-left lg:z--button-base-lg-icon-height lg:z--button-base-lg-icon-width',
        rightIcon:
          'lg:z--button-lg-right-icon-margin-right lg:z--button-base-lg-icon-height lg:z--button-base-lg-icon-width',
      },
      xl: {
        root: 'lg:lg:z--button-xl-padding-left lg:z--button-xl-padding-right',
        content: 'lg:z--button-xl-gap lg:z--button-xl-font-size',
        leftIcon:
          'lg:z--button-xl-left-icon-margin-left lg:z--button-base-xl-icon-height lg:z--button-base-xl-icon-width',
        rightIcon:
          'lg:z--button-xl-right-icon-margin-right lg:z--button-base-xl-icon-height lg:z--button-base-xl-icon-width',
      },
    },
    sizeXl: {
      sm: {
        root: 'xl:xl:z--button-sm-padding-left xl:z--button-sm-padding-right',
        content: 'xl:z--button-sm-gap xl:z--button-sm-font-size',
        leftIcon:
          'xl:z--button-sm-left-icon-margin-left xl:z--button-base-sm-icon-height xl:z--button-base-sm-icon-width',
        rightIcon:
          'xl:z--button-sm-right-icon-margin-right xl:z--button-base-sm-icon-height xl:z--button-base-sm-icon-width',
      },
      md: {
        root: 'xl:xl:z--button-md-padding-left xl:z--button-md-padding-right',
        content: 'xl:z--button-md-gap xl:z--button-md-font-size',
        leftIcon:
          'xl:z--button-md-left-icon-margin-left xl:z--button-base-md-icon-height xl:z--button-base-md-icon-width',
        rightIcon:
          'xl:z--button-md-right-icon-margin-right xl:z--button-base-md-icon-height xl:z--button-base-md-icon-width',
      },
      lg: {
        root: 'xl:xl:z--button-lg-padding-left xl:z--button-lg-padding-right',
        content: 'xl:z--button-lg-gap xl:z--button-lg-font-size',
        leftIcon:
          'xl:z--button-lg-left-icon-margin-left xl:z--button-base-lg-icon-height xl:z--button-base-lg-icon-width',
        rightIcon:
          'xl:z--button-lg-right-icon-margin-right xl:z--button-base-lg-icon-height xl:z--button-base-lg-icon-width',
      },
      xl: {
        root: 'xl:xl:z--button-xl-padding-left xl:z--button-xl-padding-right',
        content: 'xl:z--button-xl-gap xl:z--button-xl-font-size',
        leftIcon:
          'xl:z--button-xl-left-icon-margin-left xl:z--button-base-xl-icon-height xl:z--button-base-xl-icon-width',
        rightIcon:
          'xl:z--button-xl-right-icon-margin-right xl:z--button-base-xl-icon-height xl:z--button-base-xl-icon-width',
      },
    },
    isFullWidth: {
      true: {
        root: 'w-full',
      },
      false: {
        root: 'w-auto',
      },
    },
    isLoading: {
      // Use opacity-0 instead of display:none or visibility:hidden to:
      // 1. Keep the text in the accessibility tree as a fallback when aria-label or aria-labelledby is not provided
      // 2. Maintain layout space to prevent button size changes during loading
      true: {
        label: 'opacity-0',
        leftIcon: 'invisible',
        rightIcon: 'invisible',
      },
    },
  },
  compoundVariants: [
    // Ghost variant uses its own padding token set (different values from filled/outlined)
    {
      variant: 'ghost',
      size: 'sm',
      class: {
        root: 'z--button-text-sm-padding-left z--button-text-sm-padding-right',
      },
    },
    {
      variant: 'ghost',
      size: 'md',
      class: {
        root: 'z--button-text-md-padding-left z--button-text-md-padding-right',
      },
    },
    {
      variant: 'ghost',
      size: 'lg',
      class: {
        root: 'z--button-text-lg-padding-left z--button-text-lg-padding-right',
      },
    },
    {
      variant: 'ghost',
      size: 'xl',
      class: {
        root: 'z--button-text-xl-padding-left z--button-text-xl-padding-right',
      },
    },
    {
      variant: 'ghost',
      sizeXs: 'sm',
      class: {
        root: 'xs:z--button-text-sm-padding-left xs:z--button-text-sm-padding-right',
      },
    },
    {
      variant: 'ghost',
      sizeXs: 'md',
      class: {
        root: 'xs:z--button-text-md-padding-left xs:z--button-text-md-padding-right',
      },
    },
    {
      variant: 'ghost',
      sizeXs: 'lg',
      class: {
        root: 'xs:z--button-text-lg-padding-left xs:z--button-text-lg-padding-right',
      },
    },
    {
      variant: 'ghost',
      sizeXs: 'xl',
      class: {
        root: 'xs:z--button-text-xl-padding-left xs:z--button-text-xl-padding-right',
      },
    },
    {
      variant: 'ghost',
      sizeSm: 'sm',
      class: {
        root: 'sm:z--button-text-sm-padding-left sm:z--button-text-sm-padding-right',
      },
    },
    {
      variant: 'ghost',
      sizeSm: 'md',
      class: {
        root: 'sm:z--button-text-md-padding-left sm:z--button-text-md-padding-right',
      },
    },
    {
      variant: 'ghost',
      sizeSm: 'lg',
      class: {
        root: 'sm:z--button-text-lg-padding-left sm:z--button-text-lg-padding-right',
      },
    },
    {
      variant: 'ghost',
      sizeSm: 'xl',
      class: {
        root: 'sm:z--button-text-xl-padding-left sm:z--button-text-xl-padding-right',
      },
    },
    {
      variant: 'ghost',
      sizeMd: 'sm',
      class: {
        root: 'md:z--button-text-sm-padding-left md:z--button-text-sm-padding-right',
      },
    },
    {
      variant: 'ghost',
      sizeMd: 'md',
      class: {
        root: 'md:z--button-text-md-padding-left md:z--button-text-md-padding-right',
      },
    },
    {
      variant: 'ghost',
      sizeMd: 'lg',
      class: {
        root: 'md:z--button-text-lg-padding-left md:z--button-text-lg-padding-right',
      },
    },
    {
      variant: 'ghost',
      sizeMd: 'xl',
      class: {
        root: 'md:z--button-text-xl-padding-left md:z--button-text-xl-padding-right',
      },
    },
    {
      variant: 'ghost',
      sizeLg: 'sm',
      class: {
        root: 'lg:z--button-text-sm-padding-left lg:z--button-text-sm-padding-right',
      },
    },
    {
      variant: 'ghost',
      sizeLg: 'md',
      class: {
        root: 'lg:z--button-text-md-padding-left lg:z--button-text-md-padding-right',
      },
    },
    {
      variant: 'ghost',
      sizeLg: 'lg',
      class: {
        root: 'lg:z--button-text-lg-padding-left lg:z--button-text-lg-padding-right',
      },
    },
    {
      variant: 'ghost',
      sizeLg: 'xl',
      class: {
        root: 'lg:z--button-text-xl-padding-left lg:z--button-text-xl-padding-right',
      },
    },
    {
      variant: 'ghost',
      sizeXl: 'sm',
      class: {
        root: 'xl:z--button-text-sm-padding-left xl:z--button-text-sm-padding-right',
      },
    },
    {
      variant: 'ghost',
      sizeXl: 'md',
      class: {
        root: 'xl:z--button-text-md-padding-left xl:z--button-text-md-padding-right',
      },
    },
    {
      variant: 'ghost',
      sizeXl: 'lg',
      class: {
        root: 'xl:z--button-text-lg-padding-left xl:z--button-text-lg-padding-right',
      },
    },
    {
      variant: 'ghost',
      sizeXl: 'xl',
      class: {
        root: 'xl:z--button-text-xl-padding-left xl:z--button-text-xl-padding-right',
      },
    },
  ],
});

const ButtonIcon = ({
  icon,
  slotClass,
}: {
  icon: ReactElement;
  slotClass: (opts?: { class?: string }) => string;
}) => {
  const iconProps = icon.props as { className?: string };

  return cloneElement(icon, {
    className: slotClass({ class: iconProps.className }),
  } as Partial<typeof icon.props>);
};

type ButtonContentProps = {
  isFocused: boolean;
  isLoading: boolean;
  elementId: string;
  ariaLabelledby: string | undefined;
  loadingIndicatorId: string;
  loadingAriaLabel: string | undefined;
  styles: ReturnType<typeof buttonStyles>;
  spinnerSize: ResponsiveOption<SpinnerSizeValue>;
  leftIcon: ReactElement | undefined;
  rightIcon: ReactElement | undefined;
  children: React.ReactNode;
};

const ButtonContent = memo(
  ({
    isFocused,
    isLoading,
    elementId,
    ariaLabelledby,
    loadingIndicatorId,
    loadingAriaLabel,
    styles,
    spinnerSize,
    leftIcon,
    rightIcon,
    children,
  }: ButtonContentProps) => {
    useLoadingAnnouncement({ elementId, isLoading, isFocused, ariaLabelledby });

    return (
      <span className={styles.content()}>
        {leftIcon && <ButtonIcon icon={leftIcon} slotClass={styles.leftIcon} />}
        {Boolean(Children.count(children) > 0) && (
          <span className={styles.label()}>{children}</span>
        )}
        {rightIcon && (
          <ButtonIcon icon={rightIcon} slotClass={styles.rightIcon} />
        )}
        {isLoading && (
          <Spinner
            id={loadingIndicatorId}
            className={styles.spinner()}
            color="inherit"
            size={spinnerSize}
            aria-label={loadingAriaLabel}
          />
        )}
      </span>
    );
  }
);

ButtonContent.displayName = 'ButtonContent';

type DefaultElementType = typeof DEFAULT_ELEMENT_TYPE;

type OmittedKeys = 'disabled';

type ButtonProps<RootComponent extends ElementType = DefaultElementType> =
  PolymorphicComponentProps<ButtonBaseProps, RootComponent, OmittedKeys>;

type ButtonComponent = PolymorphicComponent<
  ButtonBaseProps,
  DefaultElementType,
  OmittedKeys
>;

const Button: ButtonComponent = (props) => {
  // Cast to ButtonProps to access all properties including those from the polymorphic component type.
  // The PolymorphicComponent generic infers a union of possible prop types, so we narrow it here.
  // Note: We assume the default 'button' element type. When using a custom `component` prop,
  // consumers are responsible for ensuring runtime correctness and type safety.
  const finalProps = props as ButtonProps;
  const {
    id,
    component: Root = DEFAULT_ELEMENT_TYPE,
    variant = 'filled',
    color = 'accent-primary',
    size: sizeProp,
    isDisabled = false,
    isLoading = false,
    loadingAriaLabel,
    leftIcon,
    rightIcon,
    isFullWidth = false,
    children,
    className,
    ref,
    ...rest
  } = finalProps;

  const { defaultOption: defaultSize, responsiveOption: responsiveSize } =
    resolveResponsiveProps(sizeProp);
  const resolvedSize = defaultSize ?? 'md';

  const styles = buttonStyles({
    variant,
    size: resolvedSize,
    sizeXs: responsiveSize?.xs,
    sizeSm: responsiveSize?.sm,
    sizeMd: responsiveSize?.md,
    sizeLg: responsiveSize?.lg,
    sizeXl: responsiveSize?.xl,
    isFullWidth,
    isLoading,
  });

  return (
    <ButtonBase
      {...rest}
      id={id}
      component={Root}
      variant={variant}
      color={color}
      size={sizeProp}
      isDisabled={isDisabled}
      isLoading={isLoading}
      loadingAriaLabel={loadingAriaLabel}
      componentName="Button"
      ref={ref}
      className={styles.root({ class: className })}
    >
      {({
        isFocused,
        rootId,
        loadingIndicatorId,
        ariaLabelledby,
        spinnerSize,
      }) => (
        <ButtonContent
          isFocused={isFocused}
          isLoading={isLoading}
          elementId={rootId}
          ariaLabelledby={ariaLabelledby}
          loadingIndicatorId={loadingIndicatorId}
          loadingAriaLabel={loadingAriaLabel}
          styles={styles}
          spinnerSize={spinnerSize}
          leftIcon={leftIcon}
          rightIcon={rightIcon}
        >
          {children}
        </ButtonContent>
      )}
    </ButtonBase>
  );
};

Button.displayName = 'Button';

export { Button, type ButtonProps, type ButtonBaseProps, buttonClasses };
